import React from 'react';
import { useForm } from 'react-hook-form';
import { useRegistration } from '../Registration';
import { useLanguage } from '../../../contexts/LanguageContext';

const Step5AdditionalInfo = () => {
  const { formData, updateFormData, nextStep, prevStep } = useRegistration();
  const { t } = useLanguage();
  const copy = t('registration.steps.additional', {});
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      day: formData.day,
      month: formData.month,
      year: formData.year,
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2,
      addressLine3: formData.addressLine3
    }
  });

  const onSubmit = (data) => {
    updateFormData(data);
    nextStep();
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);

  const months = Array.isArray(copy.birthDate?.months) ? copy.birthDate.months : [];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {copy.title}
        </h2>
        <p className="text-gray-600 text-center mb-8">
          {copy.description}
        </p>
      </div>

      {/* Date of Birth */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {copy.birthDate?.label}
        </label>
        <div className="grid grid-cols-3 gap-4">
          {/* Day */}
          <div>
            <label htmlFor="day" className="sr-only">{copy.birthDate?.fields?.day}</label>
            <select
              {...register('day', { required: t('validation.dayRequired') })}
              id="day"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            >
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day.toString().padStart(2, '0')}>
                  {day.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
            {errors.day && (
              <p className="mt-1 text-sm text-red-600">{errors.day.message}</p>
            )}
          </div>

          {/* Month */}
          <div>
            <label htmlFor="month" className="sr-only">{copy.birthDate?.fields?.month}</label>
            <select
              {...register('month', { required: t('validation.monthRequired') })}
              id="month"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            >
              <option value="">{copy.birthDate?.fields?.month}</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            {errors.month && (
              <p className="mt-1 text-sm text-red-600">{errors.month.message}</p>
            )}
          </div>

          {/* Year */}
          <div>
            <label htmlFor="year" className="sr-only">{copy.birthDate?.fields?.year}</label>
            <select
              {...register('year', { required: t('validation.yearRequired') })}
              id="year"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
            >
              <option value="">Year</option>
              {years.map(year => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
            {errors.year && (
              <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{copy.address?.title}</h3>
        
        {/* Address Line 1 */}
        <div>
          <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.address?.line1?.label}
          </label>
          <input
            {...register('addressLine1', { required: t('validation.addressRequired') })}
            type="text"
            id="addressLine1"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder={copy.address?.line1?.placeholder}
          />
          {errors.addressLine1 && (
            <p className="mt-1 text-sm text-red-600">{errors.addressLine1.message}</p>
          )}
        </div>

        {/* Address Line 2 */}
        <div>
          <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.address?.line2?.label}
          </label>
          <input
            {...register('addressLine2')}
            type="text"
            id="addressLine2"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder={copy.address?.line2?.placeholder}
          />
        </div>

        {/* Address Line 3 */}
        <div>
          <label htmlFor="addressLine3" className="block text-sm font-medium text-gray-700 mb-2">
            {copy.address?.line3?.label}
          </label>
          <input
            {...register('addressLine3')}
            type="text"
            id="addressLine3"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            placeholder={copy.address?.line3?.placeholder}
          />
        </div>
      </div>

      {/* Privacy Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">{copy.privacy?.title}</h3>
            <p className="mt-1 text-sm text-yellow-700">
              {copy.privacy?.description}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={prevStep}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
        >
          {t('registration.previous')}
        </button>
        
        <button
          type="submit"
          className="px-6 py-3 bg-prayer-red text-white rounded-lg font-medium hover:bg-prayer-red-light transition-all duration-200"
        >
          {t('registration.next')}
        </button>
      </div>
    </form>
  );
};

export default Step5AdditionalInfo;
